import React, { useEffect, useState } from "react";
import { Lock, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, formatMoney2 } from "../../lib/api";
import { ExportButtons } from "../../lib/exports";

const fecha = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "");

export const CierreBar = ({ d, importId, onClosed }) => {
  const [busy, setBusy] = useState(false);
  if (d.cerrado) {
    return (
      <div className="rounded-lg p-4 mb-6 border border-slate-900 bg-slate-900 text-white flex items-center gap-3 text-sm" data-testid="grupal-cerrado-banner">
        <Lock size={16} /> <span><b>Mes cerrado</b> el {fecha(d.closed_at)}{d.closed_by ? ` por ${d.closed_by}` : ""}. Las cifras quedan guardadas para las nóminas y ya no cambian.</span>
      </div>
    );
  }
  const cerrar = async () => {
    if (!window.confirm(`¿Cerrar el incentivo de ${d.mes_label}? Se guardará la bolsa de ${formatMoney2(d.bolsa)} y el reparto por empleado. El cierre es definitivo y no se podrá reabrir.`)) return;
    setBusy(true);
    try {
      await api.post("/incentivo-grupal/cerrar", { import_id: importId });
      toast.success("Mes cerrado y guardado");
      onClosed();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cerrar el mes");
    } finally { setBusy(false); }
  };
  return (
    <div className="card p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="grupal-cierre-bar">
      <span className="text-sm text-slate-600">Cuando el reparto sea el definitivo, cierra el mes para dejarlo registrado para las nóminas. <b>El cierre no se puede deshacer.</b></span>
      <button onClick={cerrar} disabled={busy || d.venta_libre_anterior == null} className="btn btn-primary" data-testid="grupal-cerrar-btn"><Lock size={14} /> Cerrar mes</button>
    </div>
  );
};

export const HistorialCierres = ({ refresh }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/incentivo-grupal/cierres").then((r) => setRows(r.data)).catch(() => {}); }, [refresh]);
  if (!rows.length) return null;
  const detalle = rows.flatMap((c) => (c.snapshot.reparto || []).map((r) => ({
    mes: c.mes_label, empleado: r.nombre || r.vendedor, importe: r.importe, cerrado: fecha(c.closed_at),
  })));
  return (
    <div className="card p-5 mt-6" data-testid="grupal-historial">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2"><History size={15} /><h3 className="font-display font-bold">Meses cerrados · registro para nóminas</h3></div>
        <ExportButtons filename="incentivo-grupal-nominas" title="Incentivo grupal · meses cerrados" sheetName="Nóminas"
                       columns={[{ header: "Mes", key: "mes" }, { header: "Empleado", key: "empleado" },
                                 { header: "Importe (€)", key: "importe", format: (v) => (v || 0).toFixed(2), align: "right" },
                                 { header: "Cerrado", key: "cerrado" }]}
                       rows={detalle} testIdPrefix="export-cierres" />
      </div>
      <table className="dense" data-testid="grupal-historial-table">
        <thead><tr><th>Mes</th><th className="num">Crecimiento</th><th className="num">Margen</th><th className="num">Bolsa</th><th>Cerrado</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.mes} data-testid={`cierre-${c.mes}`}>
              <td className="font-semibold">{c.mes_label}</td>
              <td className="num">{c.snapshot.crecimiento == null ? "—" : `${c.snapshot.crecimiento.toLocaleString("es-ES")} %`}</td>
              <td className="num">{c.snapshot.margen == null ? "—" : `${c.snapshot.margen.toLocaleString("es-ES")} %`}</td>
              <td className="num font-bold text-emerald-700">{formatMoney2(c.snapshot.bolsa)}</td>
              <td className="text-xs text-slate-500">{fecha(c.closed_at)} · {c.closed_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
